import axios from "axios";

// Classifies failures from POST /analysis/analyzeembed into user-facing categories.
//
// Server response shapes (see functions/src/utility/embedAnalysis.js and api.ts):
//   200 { code: "EMPTY_STT", message }                          -> no speech detected
//   500 { message, status, details: { code: "STT_FAILURE" } }    -> speech-to-text failed
//   500 { message, status, details: { code: "con-ana-log" } }    -> analysis failed after transcription
//   500 { message, status, details: "Permission Denied" | ... }  -> request validation failed
//   401/403                                                      -> not signed in / not allowed

export type AnalysisErrorType =
  | "NO_SPEECH" // recording had no detectable speech
  | "STT_FAILURE" // the server could not transcribe the recording
  | "ANALYSIS_FAILURE" // transcription worked, scoring failed
  | "NOT_AUTHORIZED" // user/story/scene validation failed; retrying won't help
  | "NETWORK_ERROR" // request never got a response
  | "SERVER_ERROR"; // any other server failure

export interface AnalysisError {
  type: AnalysisErrorType;
  message: string;
  canResubmit: boolean; // true when re-sending the same recording might succeed
  details?: any; // for logging only
}

const MESSAGES: Record<AnalysisErrorType, string> = {
  NO_SPEECH: "We could not detect anyone speaking. Please try again.",
  STT_FAILURE: "We had trouble processing your recording. Please record your response again.",
  ANALYSIS_FAILURE:
    "We heard your response but couldn't finish analyzing it. Please record your response again.",
  NOT_AUTHORIZED:
    "This scenario isn't available for your account. Please contact support if you think this is a mistake.",
  NETWORK_ERROR: "We couldn't reach the server. Please check your internet connection and try again.",
  SERVER_ERROR: "Something went wrong on our end. Please try again.",
};

const makeError = (type: AnalysisErrorType, details?: any): AnalysisError => ({
  type,
  message: MESSAGES[type],
  canResubmit: type === "NETWORK_ERROR",
  details,
});

// A 2xx response that carries no analyses (normally { code: "EMPTY_STT" })
export const classifyEmptyAnalysisResponse = (data: any): AnalysisError =>
  makeError("NO_SPEECH", { receivedData: data });

// An exception thrown by the axios call
export const classifyAnalysisError = (error: unknown): AnalysisError => {
  if (!axios.isAxiosError(error)) {
    return makeError("SERVER_ERROR", { error });
  }
  const { response } = error;
  if (!response) {
    return makeError("NETWORK_ERROR", { code: error.code, message: error.message });
  }

  const { status } = response;
  const data: any = response.data;
  const serverDetails = data?.details ?? data?.error?.details;
  const serverCode = typeof serverDetails === "object" ? serverDetails?.code : undefined;
  const logDetails = { status, serverCode, serverDetails };

  if (status === 401 || status === 403) return makeError("NOT_AUTHORIZED", logDetails);
  if (serverCode === "EMPTY_STT") return makeError("NO_SPEECH", logDetails);
  if (serverCode === "STT_FAILURE") return makeError("STT_FAILURE", logDetails);
  if (serverCode === "con-ana-log") return makeError("ANALYSIS_FAILURE", logDetails);
  // validation failures in analyze_utterance2 send a plain string as the details
  if (typeof serverDetails === "string") return makeError("NOT_AUTHORIZED", logDetails);
  return makeError("SERVER_ERROR", logDetails);
};
